import { ColumnType } from 'antd/es/table';
import { ReactComponent as SwapIcon } from '@Icons/additional/swap.svg';
import {
  PriceComparisonDto,
  ComparisonSheetSelectedItem,
  PriceSelectionItemDto,
  ComparisonSheetDisabledItems,
} from '../ComparisonSheet.types';
import { VendorQuantitySplitFormValues } from '../modals/VendorQuantitySplit/VendorQuantitySplit';

export function withSortIcon<T>(config: ColumnType<T>): ColumnType<T> {
  return {
    ...config,
    sortIcon: () => {
      return config.sorter ? <SwapIcon /> : null;
    },
  };
}

export const isComparisonSheetRecordChecked = (
  selectedOffersIDs: ComparisonSheetSelectedItem[] | undefined,
  matId: number,
  vendorId: number
): boolean => {
  return Boolean(
    selectedOffersIDs?.find(
      (selectedRecord) =>
        selectedRecord.matId === matId && selectedRecord.vendorId === vendorId
    )
  );
};

export const composeSelectedOffers = (
  offers: PriceComparisonDto[],
  selectedIDs: ComparisonSheetSelectedItem[]
): PriceSelectionItemDto[] => {
  return offers.reduce((allSelectedOffers, offer) => {
    const selectedForMaterial = offer.vendorOffers.filter((vOffer) =>
      isComparisonSheetRecordChecked(selectedIDs, offer.matId, vOffer.vendorId)
    );
    if (selectedForMaterial.length) {
      allSelectedOffers.push({ ...offer, vendorOffers: selectedForMaterial });
      return allSelectedOffers;
    }
    return allSelectedOffers;
  }, [] as PriceSelectionItemDto[]);
};

// - Compute the “raw” allocation for each item by doing:
// raw = (percentage / 100) * totalNumber
// - Apply a “threshold rule”: if the raw value is less than 1 then set the provisional allocation to 0
// (i.e. “ignore” items that would get less than one whole unit).
// - For items above the threshold (raw ≥ 1), take the floor (i.e. the integer part) as a base allocation and also keep track of the rest (raw minus floor).
// - Sum the base allocations. If this sum is less than totalNumber, then there is a remainder that must be distributed.
// – Only items that were “eligible” (raw ≥ 1) are allowed to receive extra units.
// – Sort these eligible items by the size of their fractional remainder (largest first) and distribute extra units one by one until the total equals totalNumber.
// Corner case: If none of the items are eligible (all raw values are less than 1), then you could assign all to the item with the highest raw value.
// Examples:
// 1. requiredQty=3, offers quantities [49,51] => output = [1,2]
// 2. requiredQty=3, offers quantities [33,67] => output = [0,3]
// 3. requiredQty=3, offers quantities [34,66] => output = [1,2]
export const getQuantitySplit = (
  requiredQty: number,
  offers: VendorQuantitySplitFormValues['offers']
): Array<
  VendorQuantitySplitFormValues['offers'][number] & { adjustedQty: number }
> => {
  const n = offers.length;
  const rawValues = offers.map((offer) => (requiredQty * offer.quantity) / 100);
  const allocation: number[] = new Array(n).fill(0);
  const eligible = []; // store { index, rest } for items with raw >= 1

  // Step 1 & 2: Compute raw and set base allocations
  for (let i = 0; i < n; i += 1) {
    if (rawValues[i] < 1) {
      allocation[i] = 0;
    } else {
      allocation[i] = Math.floor(rawValues[i]);
      eligible.push({
        index: i,
        rest: rawValues[i] - Math.floor(rawValues[i]),
      });
    }
  }

  // Sum of base allocations
  const allocatedSum = allocation.reduce((sum, num) => sum + num, 0);
  let remaining = requiredQty - allocatedSum;

  // Step 3: Distribute the remaining
  if (remaining > 0) {
    // If no item is eligible, assign all to the one with the highest raw value
    if (eligible.length === 0) {
      const maxIndex = rawValues.indexOf(Math.max(...rawValues));
      allocation[maxIndex] = requiredQty;
    } else {
      // Sort eligible items by rest part descending
      eligible.sort((a, b) => b.rest - a.rest);
      // Distribute extra units one by one
      let idx = 0;
      while (remaining > 0) {
        allocation[eligible[idx].index] += 1;
        remaining -= 1;
        idx = (idx + 1) % eligible.length;
      }
    }
  }
  // return allocation as adjustedQty for each offer
  const result = allocation.map((item, index) => ({
    ...offers[index],
    adjustedQty: item,
  }));

  return result;
};

export const getSplitMaterialIDs = (
  selectedItems?: ComparisonSheetSelectedItem[]
): number[] => {
  if (selectedItems) {
    const countMap = new Map();

    // Count occurrences of each id
    selectedItems.forEach(({ matId }) => {
      countMap.set(matId, (countMap.get(matId) || 0) + 1);
    });

    const splitMaterials = Array.from(countMap.entries())
      .filter(([, count]) => count >= 2)
      .map(([id]) => Number(id));
    return splitMaterials;
  }
  return [];
};

export const getIsCancellableByAccess = (access?: number): boolean => {
  return access !== 3;
};

export const getDisabledItemsByAccess = (
  access?: number
): ComparisonSheetDisabledItems => {
  let disableSplit = true;
  let disableSelection = false;
  let disableUnlockPricing = false;
  let disableTenderCancel = false;
  let disableApprover = true;

  if (access === 1 || access === 6 || access === 7) {
    disableSplit = false;
  }
  if (access === 2) {
    disableSelection = true;
    disableUnlockPricing = true;
  }
  if (access === 3 || access === 4) {
    disableApprover = false;
  }
  if (access === 3) {
    disableTenderCancel = true;
  }
  return {
    disableSplit,
    disableSelection,
    disableUnlockPricing,
    disableTenderCancel,
    disableApprover,
  };
};
